<template>
  <div class="flex flex-wrap lg:gap-4 pt-10 pb-2 border-t">
    <div class="min-w-[200px] basis-1/2 lg:basis-auto">
      <span :title="review.user.display_name">{{ review.user.display_name }}</span>
      <div v-if="review.verified_buyer" class="flex">
        <img src="/images/yotpo/verified.svg" class="w-4 mr-1" width="16" height="16" alt="Verified Buyer" />
        <span class="ml-1 text-sm">Verified Buyer</span>
      </div>
    </div>

    <div class="flex justify-end flex-grow lg:flex-grow-0 basis-1/2 lg:basis-auto lg:order-3">
      {{ dateToLocaleEnUS(review.created_at) }}
    </div>

    <div class="flex flex-col flex-grow basis-full lg:basis-1/2">
      <div class="flex flex-col lg:flex-row items-start lg:mb-4 lg:order-0">
        <div class="flex mb-4 lg:mb-0 order-0 basis-1/12 items-center mt-1">
          <div v-for="i in 5" :key="i" class="w-[18px] h-[18px] mr-2">
            <img v-if="i <= review.score" src="/images/star-full-bw.svg" width="18" height="18" alt="full star" />
            <img v-else src="/images/star-empty-bw.svg" width="18" height="18" alt="empty star" />
          </div>
        </div>

        <div class="order-1 mb-4 lg:mb-0 self-start basis-11/12 font-bold text-start text-lg mt-px">
          {{ review.title }}
        </div>
      </div>

      <div v-if="review.images_data && review.images_data.length > 0" class="flex flex-wrap gap-2 mb-4 lg:order-2">
        <button
          v-for="(image, index) in review.images_data.slice(0, 4)"
          :key="image.id"
          aria-label="Open review image"
          class="hover:outline-2 hover:outline relative"
          @click="openReviewModal(review, index)"
        >
          <img
            :src="image.thumb_url"
            class="w-20 lg:w-[100px] h-20 lg:h-[100px] object-cover"
            width="100"
            height="100"
            :alt="`Review image thumbnail ${index + 1}`"
          />
          <div
            v-if="index === 3 && review.images_data.length > 4"
            class="absolute text-2xl font-normal inset-0 bg-black opacity-75 flex items-center justify-center text-white"
          >
            +{{ review.images_data.length - 3 }}
          </div>
        </button>
      </div>

      <div class="lg:mr-5 lg:order-1">
        <span>
          <InlineHtml :text="truncateContent(review.content)" class="text-sm lg:text-base" />
        </span>
        <button
          v-if="review.content.length > 300"
          class="text-sm inline-block underline font-bold ml-2"
          @click="isExpanded = !isExpanded"
        >
          {{ isExpanded ? 'Read less' : 'Read more' }}
        </button>
      </div>
    </div>

    <div class="mt-10 flex w-full justify-end lg:mt-2 order-4">
      <div class="mr-2">Was this review helpful?</div>
      <button aria-label="Vote up" class="flex items-center mr-2">
        <img
          :src="userVotes[review.id] === 'up' ? '/images/yotpo/voteUpFill.svg' : '/images/yotpo/voteUp.svg'"
          :class="{ 'opacity-50': isWaiting, 'cursor-not-allowed': isWaiting }"
          class="w-4 mr-1"
          width="16"
          height="16"
          alt="vote up"
          @click="voteUp(review.id)"
        />
        <span>{{ userVotes[review.id] === 'up' ? review.votes_up + 1 : review.votes_up }}</span>
      </button>

      <button aria-label="Vote down" class="flex items-center">
        <img
          :src="userVotes[review.id] === 'down' ? '/images/yotpo/voteDownFill.svg' : '/images/yotpo/voteDown.svg'"
          :class="{ 'opacity-50': isWaiting, 'cursor-not-allowed': isWaiting }"
          class="w-4 mr-1"
          width="16"
          height="16"
          alt="vote down"
          @click="voteDown(review.id)"
        />
        <span>{{ userVotes[review.id] === 'down' ? review.votes_down + 1 : review.votes_down }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
// We have to manually import from VueUse because Nitro's auto-import of their userStorage overrides it
import { useStorage } from '@vueuse/core'

const { review } = defineProps<{
  review: YotpoReview
}>()

const emit = defineEmits(['onOpenModal'])

const userVotes = ref<Record<string, 'up' | 'down' | null>>({})
const isExpanded = ref<boolean>(false)
const isWaiting = ref(false)

useStorage('yotpoVotes', userVotes)

async function voteUp(id: number) {
  if (isWaiting.value) return
  isWaiting.value = true

  try {
    // If the user has already voted down, undo the down vote
    if (userVotes.value[id] === 'down') {
      await $fetch<YotpoVoteResponse>(`https://api.yotpo.com/reviews/${id}/vote/down/true`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
    }

    // If the user has already voted up, use the undo endpoint otherwise use the upvote endpoint
    const url =
      userVotes.value[id] === 'up'
        ? `https://api.yotpo.com/reviews/${id}/vote/up/true`
        : `https://api.yotpo.com/reviews/${id}/vote/up`

    await $fetch<YotpoVoteResponse>(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })

    // If the user has already voted up, remove the up vote otherwise set the vote to up
    userVotes.value[id] === 'up' ? (userVotes.value[id] = null) : (userVotes.value[id] = 'up')
  } catch (err) {
    //
  } finally {
    isWaiting.value = false
  }
}

async function voteDown(id: number) {
  if (isWaiting.value) return
  isWaiting.value = true

  try {
    // If the user has already voted up, undo the up vote
    if (userVotes.value[id] === 'up') {
      await $fetch<YotpoVoteResponse>(`https://api.yotpo.com/reviews/${id}/vote/up/true`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
    }

    // If the user has already voted down, use the undo endpoint otherwise use the downvote endpoint
    const url =
      userVotes.value[id] === 'down'
        ? `https://api.yotpo.com/reviews/${id}/vote/down/true`
        : `https://api.yotpo.com/reviews/${id}/vote/down`

    await $fetch<YotpoVoteResponse>(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })

    // If the user has already voted down, undo the down vote otherwise set the vote to down
    userVotes.value[id] === 'down' ? (userVotes.value[id] = null) : (userVotes.value[id] = 'down')
  } catch (err) {
    //
  } finally {
    isWaiting.value = false
  }
}

function truncateContent(content: string) {
  if (content.length > 300 && !isExpanded.value) return content.substring(0, 300) + '...'
  return content
}

function openReviewModal(review: YotpoReview, index: number) {
  emit('onOpenModal', review, index)
}
</script>
